import { toPath } from 'lodash';
import * as Yup from 'yup';

import { FORM_IDS } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/PhoneConfigurations/PhoneSurveyConfiguration/utils/constants';
import { validateNoneEmptyHtmlContent } from '~/Utils/validationUtils';

export interface AllowSavingState {
  welcome: boolean;
  response_line: boolean;
  illegal: boolean;
  ending: boolean;
  questions: boolean[];
}

const createMessageTextValidation = () =>
  Yup.string()
    .nullable()
    .when('configuration_type', {
      is: 'text_message',
      then: Yup.string().nullable().test('required', 'Required', validateNoneEmptyHtmlContent),
    });

const createConfigurationTypeValidation = () =>
  Yup.string().oneOf(['text_message', 'uploaded_text_message']).required('Required');

const createStoredFileValidation = (allowSaving: boolean) =>
  Yup.mixed().when('configuration_type', {
    is: 'uploaded_text_message',
    then: allowSaving ? Yup.mixed() : Yup.mixed().required('Please select a file'),
  });

const createQuestionStoredFileValidation = (allowSavingWithoutNewFiles: AllowSavingState) =>
  Yup.mixed().when('configuration_type', {
    is: 'uploaded_text_message',
    then: Yup.mixed().test('file-required', 'Please select a file', function (value) {
      const fieldPathAsArray = toPath(this.path);
      const questionIndex = parseInt(fieldPathAsArray[1]);
      return allowSavingWithoutNewFiles.questions[questionIndex] || !!value;
    }),
  });

const createMessageConfigurationSchema = (allowSaving: boolean) =>
  Yup.object().shape({
    configuration_type: createConfigurationTypeValidation(),
    message_text: createMessageTextValidation(),
    stored_file_id: createStoredFileValidation(allowSaving),
  });

const createQuestionConfigurationSchema = (allowSavingWithoutNewFiles: AllowSavingState) =>
  Yup.object().shape({
    configuration_type: createConfigurationTypeValidation(),
    message_text: createMessageTextValidation(),
    stored_file_id: createQuestionStoredFileValidation(allowSavingWithoutNewFiles),
  });

const createAnswerOptionsSchema = () =>
  Yup.array()
    .of(
      Yup.object().shape({
        content_configuration: Yup.object().shape({
          message_text: Yup.string().nullable().required('Required'),
        }),
        position: Yup.number().required('Required'),
      })
    )
    .min(1, 'At least one option is required')
    .max(5, 'Maximum 5 options allowed')
    .required('Required');

export const createValidationSchema = (allowSavingWithoutNewFiles: AllowSavingState) =>
  Yup.object().shape({
    [FORM_IDS.WELCOME_MESSAGE_CONFIGURATION]: createMessageConfigurationSchema(allowSavingWithoutNewFiles.welcome),
    [FORM_IDS.RESPONSE_LINE_CONFIGURATION]: createMessageConfigurationSchema(allowSavingWithoutNewFiles.response_line),
    [FORM_IDS.ILLEGAL_RESPONSE_TEXT_CONFIGURATION]: createMessageConfigurationSchema(
      allowSavingWithoutNewFiles.illegal
    ),
    [FORM_IDS.ENDING_MESSAGE_CONFIGURATION]: createMessageConfigurationSchema(allowSavingWithoutNewFiles.ending),
    [FORM_IDS.QUESTIONS]: Yup.array()
      .of(
        Yup.object().shape({
          question_configuration: createQuestionConfigurationSchema(allowSavingWithoutNewFiles),
          answer_options: createAnswerOptionsSchema(),
        })
      )
      .min(1, 'At least one question is required')
      .max(5, 'Maximum 5 questions allowed')
      .required('Required'),
  });
